// The sovereignty layer: the owner byte of every cell, and the traced
// OUTLINE of §4.2 that borders each claim (R495, R532).
//
// `../outline-geo.ts` traces, relaxes and cuts the corners of every loop of
// one faction, then hands this layer a fill mesh and a stroke mesh, and the
// two programs of `../shaders/geo.ts` draw them. One `MeshBuffer` pair per
// faction holds the geometry; a flip marks the two factions it touches, and
// the rebuild runs once per frame.
//
// The kernel field of R431 was the other border of this layer until the
// owner retired it on 2026-11-03 (R532): its texture, its pocket and orphan
// walks and the fused pass of R488 left the tree with it.
//
// The layer view's two terms (R456, R501) are SKIPPED PASSES: `setFills`
// and `setBorders` say whether the wash and the glow draw, and no program
// is compiled for either. The arrival writes `uArrival` through `setAlpha`
// (R838), so a fade compiles no second program either (R1578).

import type { Engine } from '../engine';
import type { DrawCtx, FrameInfo, Layer } from '../layer';
import type { WorldPass } from '../gfx/worldquad';
import type { MeshBuffer } from '../gfx/types';
import { UniformValues } from '../gfx/uniforms';
import {
  GEO_FILL_ATTRIBS, GEO_FILL_UNIFORMS, GEO_STROKE_ATTRIBS, GEO_STROKE_UNIFORMS,
  geoFillProgram, geoStrokeProgram,
} from '../shaders/geo';
import {
  FILL_STRIDE, STROKE_STRIDE, buildFactionGeometry, rockFilledOwners,
} from '../outline-geo';
import { DEFAULT_KNOBS, onKnobs, type Knobs } from '../knobs';
import type { PlanePlacement } from './terrain';
import { NUM_FACTIONS } from '../../gen/balance';
import { TERRITORY_PULSE_HZ } from '../../gen/presentation';
import { clamp01 } from '../../render/recipe-kit';

/**
 * The border pulse: `0.75 + 0.25·sin(1.4·t)` on the border alpha. The
 * layer writes a new value only when `floor(t·TERRITORY_PULSE_HZ)` changes
 * (ten steps a second, not sixty). The PixiJS layer this replaced pulsed
 * the same way, because a smoother pulse would be a different look and not
 * a better one.
 */
export function pulseAt(timeS: number): number {
  return 0.75 + 0.25 * Math.sin(1.4 * timeS);
}

/** The unpulsed multiplier. `#pulse=0` freezes the border on it. */
const PULSE_OFF = 1;

/** The clock the retrace is timed on. A test environment holds none. */
const now = (): number => (typeof performance === 'undefined' ? 0 : performance.now());

/** What the traced OUTLINE border cost, for the stats line (R495). */
export interface GeoStats {
  /** Faction retraces since the layer was built. */
  rebuilds: number;
  /** What the last frame's retraces cost, in ms. */
  lastMs: number;
  /** Smoothed loop points every faction holds. */
  points: number;
}

/** The two meshes of one faction, and the size of the trace behind them. */
interface FactionMesh {
  fill: MeshBuffer;
  stroke: MeshBuffer;
  points: number;
}

/**
 * The layer view's two territory terms (R456, R501). Both are on unless a
 * caller turns one off.
 */
export interface TerritoryView {
  fills?: boolean;
  borders?: boolean;
}

/** What the layer needs from the terrain layer: where its plane sits. */
interface PlaneSource {
  readonly planePlacement: PlanePlacement | null;
}

export class TerritoryLayer implements Layer {
  readonly name = 'territory';

  /**
   * The layer view's merged wish: the keys turn the wash and the glow off
   * and on (R456, R501). Each setter writes its own field alone, so a caller
   * never drops another one's term.
   */
  private readonly view: TerritoryView = {};
  /** The two programs of the traced outline (R495). */
  private readonly fillPass: WorldPass;
  private readonly strokePass: WorldPass;
  private readonly uGeoFill = new UniformValues(GEO_FILL_UNIFORMS);
  private readonly uGeoStroke = new UniformValues(GEO_STROKE_UNIFORMS);
  /** The sets the arrival fades: the wash and the glow (R838, R1578). */
  private readonly arrivalSets = [this.uGeoFill, this.uGeoStroke] as const;
  private readonly stopKnobs: () => void;
  private shown = false;
  private knobs: Knobs = DEFAULT_KNOBS;
  /** The last pulse step written, so the uniform moves ten times a second. */
  private pulseStep = -1;
  /** What `uRockPlane` last carried, so the kernel path writes nothing. */
  private rockPlane = 0;

  /** One mesh pair per faction, at index `id - 1` (R495). */
  private readonly geo: FactionMesh[] = [];
  /** Which factions owe a retrace. A flip marks the two it touches. */
  private readonly geoDirty = new Set<number>();
  /** The arrival's hold on the retrace (`setDeferred`). */
  private deferred = false;
  /**
   * The owner plane of a season timelapse frame, or null for the live map
   * (R1620). While it stands, the traced outline draws it in the place of
   * the live owners; the live owners keep taking every flip.
   */
  private frame: Uint8Array | null = null;

  /** What the traced outline cost, for the stats line. */
  readonly geoStats: GeoStats = { rebuilds: 0, lastMs: 0, points: 0 };

  constructor(private readonly engine: Engine) {
    this.fillPass = engine.newWorldPass(geoFillProgram());
    this.fillPass.setExtra((p) => this.uGeoFill.apply(p));
    this.strokePass = engine.newWorldPass(geoStrokeProgram());
    this.strokePass.setExtra((p) => this.uGeoStroke.apply(p));
    // The mesh pairs are built here and never again: a faction that owns
    // nothing holds two empty buffers, which cost no GPU memory and draw
    // nothing. `MeshBuffer` keeps its two arrays, so a context restore
    // refills the GPU objects and this layer owes the geometry nothing.
    for (let id = 1; id <= NUM_FACTIONS; id++) {
      this.geo.push({
        fill: engine.newMesh(FILL_STRIDE, GEO_FILL_ATTRIBS),
        stroke: engine.newMesh(STROKE_STRIDE, GEO_STROKE_ATTRIBS),
        points: 0,
      });
    }
    this.stopKnobs = onKnobs((k) => this.applyKnobs(k));
  }

  /**
   * The owner byte of every cell, `row*COLS + col`, faction 0..5.
   *
   * It is the engine texture's own array, so a write to it is a write to
   * what the shader reads. Callers that only look — the frontier test, the
   * tooltip — read it here rather than keeping a second copy that a flip
   * could leave behind.
   */
  get owners(): Uint8Array { return this.engine.owners.data as Uint8Array; }

  /** The plane the traced outline draws: a timelapse frame, or the live
   *  owners (R1620). */
  private get drawnOwners(): Uint8Array { return this.frame ?? this.owners; }

  /**
   * Shows the owner plane of one timelapse frame, or the live map again
   * with null (WAR · SOVEREIGNTY, card 09 of round 076, R1620). The plane
   * holds an owner byte per cell, ridge cells unowned, as the live plane
   * does; a plane of another size is refused and the live map stays.
   *
   * The traced outline answers a frame the way it answers a flip: only a
   * faction whose cells moved owes a retrace, so a step of the player
   * costs the factions that one step moved.
   */
  showFrame(plane: Uint8Array | null): void {
    if (plane === null) {
      if (this.frame === null) return;
      this.frame = null;
      this.markAllGeoDirty();
      return;
    }
    const live = this.owners;
    if (plane.length !== live.length) return;
    const was = this.frame ?? live;
    const next = this.frame ?? new Uint8Array(live.length);
    for (let i = 0; i < plane.length; i++) {
      const from = was[i];
      const to = plane[i];
      if (from !== to) {
        if (from !== 0) this.geoDirty.add(from);
        if (to !== 0) this.geoDirty.add(to);
      }
      next[i] = to;
    }
    this.frame = next;
  }

  /** Is the owner plane filled? The layer draws nothing before the hello. */
  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /** The whole picture, from `Hello.owners` or from a full state frame. */
  applyOwners(bytes: Uint8Array): void {
    this.engine.owners.set(bytes);
    this.shown = true;
    this.markAllGeoDirty();
  }

  /** Every faction owes a retrace, because the whole owner plane moved. */
  private markAllGeoDirty(): void {
    for (let id = 1; id <= this.geo.length; id++) this.geoDirty.add(id);
  }

  /**
   * Tells the layer the terrain mask has landed. The outline reads the rock
   * plane (R496, R498), so every faction owes a retrace. The bytes are read
   * from the engine's own plane, which the terrain layer writes.
   */
  setBlocked(): void {
    this.markAllGeoDirty();
  }

  /**
   * One sovereignty change (protocol v15's `State.flips`).
   *
   * One byte and the two factions the cell leaves and joins. The caller
   * does the rest of what a flip means — the ring, the minimap, the WAR
   * staleness — because none of that is territory's business.
   */
  applyFlip(index: number, to: number): void {
    const cols = this.engine.env.cols;
    const col = index % cols;
    const row = (index / cols) | 0;
    // The old owner must be read before the write takes it away (R495).
    // While a timelapse frame stands, the outline draws the frame, and the
    // return to the live map retraces every faction (R1620).
    const from = this.owners[index];
    if (this.frame === null) {
      if (from !== 0) this.geoDirty.add(from);
      if (to !== 0) this.geoDirty.add(to);
    }
    this.engine.owners.setCell(col, row, to);
  }

  /**
   * Holds the retrace while the arrival's wave plays (R1533). A flip still
   * writes the owners and marks its factions; the retrace runs on the first
   * update after the hold lifts, with every flip of the wait.
   */
  setDeferred(on: boolean): void { this.deferred = on; }

  update(frame: FrameInfo): void {
    if (!this.deferred) this.walk();
    const step = Math.floor(frame.timeS * TERRITORY_PULSE_HZ);
    if (step === this.pulseStep) return;
    this.pulseStep = step;
    const pulse = this.knobs.pulse === 1 ? pulseAt(step / TERRITORY_PULSE_HZ) : PULSE_OFF;
    this.uGeoStroke.set('uPulse', pulse);
  }

  /** The retrace that a flip stales. */
  private walk(): void {
    this.rebuildGeometry();
  }

  draw(ctx: DrawCtx): void {
    this.writeRockPlane();
    this.drawGeometry(ctx);
  }

  // -- the layer view (R456, R501) -----------------------------------------

  /** The sovereignty wash, on or off (`j`). */
  setFills(on: boolean): void { this.view.fills = on; }

  /** The sovereignty border, on or off (`k`). */
  setBorders(on: boolean): void { this.view.borders = on; }

  /** What the layer's merged wish is, for the tests and for `__debug`. */
  get variantOpts(): Readonly<TerritoryView> { return this.view; }

  /**
   * The arrival's territory alpha (R838), 0 to 1. The fade is a factor on
   * every colour the wash and the glow write (R1578).
   */
  setAlpha(a: number): void {
    const v = clamp01(a);
    for (const set of this.arrivalSets) set.set('uArrival', v);
  }

  /** The terrain layer, as much of it as the rock mask must ask. */
  private terrainLayer(): PlaneSource | null {
    const layer = this.engine.layerNamed('terrain');
    if (!layer || !('planePlacement' in layer)) return null;
    return layer as unknown as PlaneSource;
  }

  dispose(): void {
    this.stopKnobs();
    this.fillPass.dispose();
    this.strokePass.dispose();
    for (const mesh of this.geo) {
      mesh.fill.dispose();
      mesh.stroke.dispose();
    }
  }

  // -- the traced outline (R495) --------------------------------------------

  /**
   * Traces every faction that owes a retrace, and uploads its two meshes.
   *
   * The trace is the whole faction and not the part a flip moved, because a
   * flip can join two loops or cut one in half and the trace of §4.2 has no
   * incremental form. It runs once a frame and not once a flip, so a burst
   * of flips in one tick costs one trace per faction (R109).
   */
  private rebuildGeometry(): void {
    if (this.geoDirty.size === 0) return;
    const env = this.engine.env;
    const st = this.geoStats;
    const t0 = now();
    // One filled plane serves every faction of the frame: a ridge one
    // claim fully surrounds belongs to that claim, so the outline draws no
    // hole round it (R498).
    const plane = rockFilledOwners(
      this.drawnOwners, this.engine.blocked.data as Uint8Array, env.cols, env.rows);
    for (const id of this.geoDirty) {
      const mesh = this.geo[id - 1];
      if (!mesh) continue;
      const built = buildFactionGeometry(plane, id, env.cols, env.rows);
      mesh.fill.set(built.fill.verts, built.fill.idx);
      mesh.stroke.set(built.stroke.verts, built.stroke.idx);
      mesh.points = built.points;
      st.rebuilds++;
    }
    this.geoDirty.clear();
    st.lastMs = now() - t0;
    let points = 0;
    for (const mesh of this.geo) points += mesh.points;
    st.points = points;
  }

  /**
   * Draws every wash. The strokes draw later, in the `borders` slot
   * (`TerritoryBorders`, R3241), over the depth tints and the speed field,
   * so the alphas of those passes never dim or tint the line.
   *
   * The washes and the strokes are not one loop, because a faction drawn
   * after its neighbour would put its wash over the neighbour's border.
   */
  private drawGeometry(ctx: DrawCtx): void {
    // The layer view reaches the wash and the border as a SKIPPED PASS
    // (R501): the pass the key turned off simply does not run.
    if (this.view.fills !== false) this.drawMeshes(ctx, this.fillPass, this.uGeoFill, 'fill');
  }

  /** Draws every stroke: the `borders` slot calls it (R3241). */
  drawBorders(ctx: DrawCtx): void {
    if (this.view.borders === false) return;
    // `lineCov` reads one device pixel across, so the strip must stand that
    // much wider than the widest pass or the outermost glow is cut off.
    this.uGeoStroke.set('uAaPad', 1 / ctx.cam.pxPerWorld);
    this.drawMeshes(ctx, this.strokePass, this.uGeoStroke, 'stroke');
  }

  /** One mesh of every faction that owns any, in faction order. */
  private drawMeshes(
    ctx: DrawCtx, pass: WorldPass, set: UniformValues, part: 'fill' | 'stroke',
  ): void {
    const env = this.engine.env;
    for (let i = 0; i < this.geo.length; i++) {
      const mesh = this.geo[i][part];
      if (mesh.indexCount === 0) continue;
      set.set('uFaction', i + 1);
      pass.draw(ctx.cam, env, mesh);
    }
  }

  // -- the rock mask's measurement knob ------------------------------------

  /**
   * Tells the wash whether the rock mask may read the terrain plane, and
   * where that plane sits (`#rock=plane`, the frame-throughput report of
   * 2026-09-04, package E).
   *
   * The plane belongs to the terrain layer and lands a moment after the
   * hello, so the answer is asked every frame rather than at the knob: until
   * the prepare has run there is nothing to read and the kernel path stands.
   * The kernel path writes no uniform at all, which is what keeps the
   * default frame the frame the measurement is read against.
   */
  private writeRockPlane(): void {
    const place = this.knobs.rock === 'plane' ? this.terrainPlane() : null;
    const on = place ? 1 : 0;
    if (on === 0 && this.rockPlane === 0) return;
    this.rockPlane = on;
    const set = this.uGeoFill;
    set.set('uRockPlane', on);
    if (place) {
      set.set('uDistLo', place.lo);
      set.set('uDistUv', place.uv);
      set.set('uDistStep', place.step);
    }
  }

  /**
   * The dash of a faction border (R1568, card 04 row a of round 075): the
   * line pass of each faction takes its own pattern of `factionmarks.toml`.
   * The World field `factionEdge` turns it on and off, and its AUTO value is
   * on. It is one uniform, so a change compiles no second program. The
   * outline lays the pattern along the length of its loop (R1577).
   */
  setEdgeDash(on: boolean): void {
    if (on === this.edgeDash) return;
    this.edgeDash = on;
    this.uGeoStroke.set('uEdgeDash', on ? 1 : 0);
  }

  /** Is the dash of the faction borders on? The uniform's default is on. */
  private edgeDash = true;

  /** Where the terrain layer put its plane, or null while it has none. */
  private terrainPlane(): PlanePlacement | null {
    return this.terrainLayer()?.planePlacement ?? null;
  }

  /** The live tuning of B.7. It re-applies on every `hashchange`. */
  private applyKnobs(k: Knobs): void {
    this.knobs = k;
    this.uGeoStroke.set('uWidthScale', k.bw);
    this.uGeoStroke.set('uInset', k.inset);
    // A frozen pulse must land at once, not at the next tenth of a second.
    this.pulseStep = -1;
  }
}

/**
 * The border lines of the territory layer, in a slot of their own
 * (`borders`, R3241, ui-map-interface.md §3). The slot stands after `depth`
 * and `speedfield`, so the alphas of the depth tints, the lanterns and the
 * current wash never dim or tint the line; the wash stays in `territory`,
 * under them. The layer owns no GPU object: it draws the strokes of its
 * host, and it shows only while the host shows, so a replay film that
 * hides `territory` hides the lines too.
 */
export class TerritoryBorders implements Layer {
  readonly name = 'borders';
  private shown = true;

  constructor(private readonly host: TerritoryLayer) {}

  get visible(): boolean { return this.shown && this.host.visible; }
  setVisible(v: boolean): void { this.shown = v; }
  /** The host does the work in its own `update`. */
  update(_frame: FrameInfo): void { /* nothing to advance */ }
  draw(ctx: DrawCtx): void { this.host.drawBorders(ctx); }
}
